import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '@/components/Layout';
import { api } from '@/utils/api';
import {
  Upload, Brain, Building2, Sparkles, ArrowRight, Loader2,
  CheckCircle2, AlertCircle, RotateCcw, FileSpreadsheet, FileText, PenLine,
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// Onboarding Hub — 4 ways to start a REVA portfolio.
//
//   A) Upload Excel/CSV     →  /excel-import     (enterprise bulk import)
//   B) Smart AI extraction  →  /smart-upload     (PDF, text, lease contracts)
//   C) Manual wizard        →  /properties (then /units, /leases)
//   D) Demo data            →  POST /api/seed/demo-portfolio  (org-scoped, safe)
//
// This page is the single answer to "how do I get started?".
// Wired from the Dashboard empty state and accessible via /onboarding.
// ─────────────────────────────────────────────────────────────────────────────

export default function Onboarding() {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const navigate = useNavigate();
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const [seedError, setSeedError] = useState(null);
  const [resetting, setResetting] = useState(false);

  const handleSeed = async () => {
    setSeeding(true); setSeedError(null); setSeedResult(null);
    try {
      const r = await api.seedDemoPortfolio();
      setSeedResult(r.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || (isAr ? 'تعذّر حقن البيانات' : 'Could not seed demo data');
      setSeedError(msg);
    } finally { setSeeding(false); }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      await api.resetDemoPortfolio();
      setSeedResult(null);
      setSeedError(null);
    } catch (e) {
      setSeedError(e?.response?.data?.detail || (isAr ? 'تعذّر الإعادة' : 'Reset failed'));
    } finally { setResetting(false); }
  };

  const cards = [
    {
      id: 'excel',
      icon: FileSpreadsheet,
      title: isAr ? 'رفع ملف Excel أو CSV' : 'Upload Excel or CSV',
      tag: isAr ? 'موصى به للمحافظ' : 'Recommended for portfolios',
      desc: isAr
        ? 'استيراد جماعي للعقارات والوحدات والمستأجرين والعقود والمدفوعات في خطوة واحدة. سحب وإفلات. التحقق التلقائي.'
        : 'Bulk-import properties, units, tenants, leases, payments in one workbook. Drag & drop. Auto-validation.',
      cta: isAr ? 'افتح أداة الاستيراد' : 'Open import tool',
      route: '/excel-import',
      testid: 'onboarding-excel',
      accent: '#00A86B',
    },
    {
      id: 'ai',
      icon: Brain,
      title: isAr ? 'الإدخال الذكي بالذكاء الاصطناعي' : 'Smart AI Data Entry',
      tag: isAr ? 'لعقود أو مستندات منفردة' : 'For contracts or single docs',
      desc: isAr
        ? 'ارفع عقدًا أو الصق نصًا، ويقوم الذكاء الاصطناعي باستخراج بيانات العقار والوحدات والمستأجرين والشروط تلقائيًّا. PDF، Word، نص عربي.'
        : 'Upload a lease contract or paste text — REVA AI extracts properties, units, tenants, and lease terms automatically. PDF, Word, Arabic text.',
      cta: isAr ? 'افتح الإدخال الذكي' : 'Open Smart Entry',
      route: '/smart-upload',
      testid: 'onboarding-ai',
      accent: '#7C3AED',
    },
    {
      id: 'manual',
      icon: PenLine,
      title: isAr ? 'إعداد يدوي' : 'Manual setup',
      tag: isAr ? 'لمُلّاك صغار' : 'For small landlords',
      desc: isAr
        ? 'أضف عقارًا، ثم الوحدات، ثم المستأجر والعقد. مناسب لمحفظة من ١-٣ عقارات. خطوة بخطوة.'
        : 'Add a property → units → tenant → lease, step by step. Best for 1–3 properties. Fast and simple.',
      cta: isAr ? 'ابدأ بعقار' : 'Start with a property',
      route: '/properties',
      testid: 'onboarding-manual',
      accent: '#D4AF37',
    },
    {
      id: 'demo',
      icon: Sparkles,
      title: isAr ? 'استكشف ببيانات تجريبية' : 'Explore with demo data',
      tag: isAr ? 'آمن · قابل للإلغاء' : 'Safe · reversible',
      desc: isAr
        ? '٤ عقارات و١٦ وحدة ومستأجرين ومدفوعات وأوامر صيانة — في مؤسستك مباشرة، معزولة بعلامة "تجريبي" ويمكن إزالتها في أي وقت.'
        : '4 properties, 16 units, tenants, payments, work orders — seeded into your org, tagged as demo, removable any time.',
      cta: isAr ? 'احقن بيانات تجريبية' : 'Inject demo data',
      action: handleSeed,
      testid: 'onboarding-demo',
      accent: '#1FD18A',
      busy: seeding,
    },
  ];

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-6" data-testid="onboarding-hub">

        {/* Header */}
        <div className="reva-card px-6 py-5">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[rgba(0,168,107,0.08)] border border-[rgba(0,168,107,0.20)] shrink-0">
              <Building2 className="h-5 w-5 text-[color:var(--reva-signal)]" strokeWidth={1.6} />
            </div>
            <div>
              <div className="text-[10.5px] reva-mono tracking-[0.16em] uppercase text-[#71777E]">
                {isAr ? 'بدء التشغيل' : 'Get started'}
              </div>
              <h1 className="font-heading text-xl sm:text-2xl text-[#0E1320] mt-1">
                {isAr ? 'كيف تريد أن تبدأ محفظتك؟' : 'How would you like to start your portfolio?'}
              </h1>
              <p className="text-[13px] text-[#4A5263] mt-1.5 leading-relaxed max-w-2xl">
                {isAr
                  ? 'اختر الطريقة المناسبة لحجم محفظتك. يمكنك دائمًا الجمع بين الطرق لاحقًا.'
                  : 'Pick the path that fits your portfolio scale. You can always combine methods later.'}
              </p>
            </div>
          </div>
        </div>

        {/* Seed feedback (only shown when demo action ran) */}
        {seedResult && (
          <div className="reva-card-emerald p-5 flex items-start gap-3" data-testid="onboarding-seed-success">
            <CheckCircle2 className="h-5 w-5 text-[color:var(--reva-signal)] shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-[13px] font-semibold text-[#0E1320]">
                {isAr ? 'تم حقن البيانات التجريبية' : 'Demo data injected successfully'}
              </div>
              <div className="text-[12px] text-[#4A5263] mt-1">
                {Object.entries(seedResult.counts || {}).map(([k, v]) => (
                  <span key={k} className="me-3 reva-mono">{v} {k.replace(/_/g, ' ')}</span>
                ))}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button onClick={() => navigate('/dashboard')} className="reva-btn-emerald-l text-[12px] py-1.5 px-3" data-testid="onboarding-go-dashboard">
                  {isAr ? 'افتح لوحة القيادة' : 'Open dashboard'} <ArrowRight className="h-3 w-3" />
                </button>
                <button onClick={handleReset} disabled={resetting} className="reva-btn-ghost-l text-[12px] py-1.5 px-3 inline-flex items-center gap-1.5" data-testid="onboarding-reset">
                  {resetting ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                  {isAr ? 'إزالة البيانات التجريبية' : 'Remove demo data'}
                </button>
              </div>
            </div>
          </div>
        )}

        {seedError && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 flex items-start gap-3" data-testid="onboarding-seed-error">
            <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
            <div className="flex-1 text-[13px] text-red-900">{seedError}</div>
          </div>
        )}

        {/* 4 cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {cards.map(c => {
            const Icon = c.icon;
            const go = () => c.action ? c.action() : navigate(c.route);
            return (
              <button
                key={c.id}
                onClick={go}
                disabled={c.busy}
                data-testid={c.testid}
                className="text-start reva-card p-5 group hover:border-[color:var(--reva-signal)]/35 transition-colors relative overflow-hidden disabled:opacity-60 disabled:cursor-wait"
              >
                <div className="absolute -top-12 -end-12 w-32 h-32 rounded-full opacity-30 group-hover:opacity-70 transition-opacity pointer-events-none" style={{ background: `radial-gradient(closest-side, ${c.accent}33, transparent 70%)` }} />
                <div className="relative flex items-start gap-3.5">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${c.accent}14`, border: `1px solid ${c.accent}40` }}>
                    {c.busy ? <Loader2 className="h-5 w-5 animate-spin" style={{ color: c.accent }} /> : <Icon className="h-5 w-5" strokeWidth={1.6} style={{ color: c.accent }} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] reva-mono tracking-[0.16em] uppercase mb-1" style={{ color: c.accent }}>{c.tag}</div>
                    <div className="text-[15px] font-semibold text-[#0E1320] leading-snug">{c.title}</div>
                    <p className="text-[12.5px] text-[#4A5263] mt-2 leading-relaxed">{c.desc}</p>
                    <div className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-medium" style={{ color: c.accent }}>
                      {c.cta} <ArrowRight className={`h-3.5 w-3.5 ${isAr ? 'rotate-180' : ''}`} />
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Quick links footer */}
        <div className="reva-card-soft px-5 py-4 flex flex-wrap items-center gap-3 text-[12px] text-[#4A5263]">
          <FileText className="h-3.5 w-3.5 text-[#71777E]" />
          <span>{isAr ? 'تحتاج مساعدة؟' : 'Need help?'}</span>
          <button onClick={() => navigate('/bulk-import')} className="text-[color:var(--reva-signal)] hover:underline" data-testid="onboarding-link-bulk">
            {isAr ? 'الاستيراد الجماعي' : 'Bulk import templates'}
          </button>
          <span className="text-[#CBD3DC]">·</span>
          <button onClick={() => navigate('/settings')} className="text-[color:var(--reva-signal)] hover:underline" data-testid="onboarding-link-settings">
            {isAr ? 'الإعدادات' : 'Settings'}
          </button>
          <span className="text-[#CBD3DC]">·</span>
          <button onClick={() => navigate('/dashboard')} className="text-[color:var(--reva-signal)] hover:underline" data-testid="onboarding-link-dashboard">
            {isAr ? 'تخطّي إلى لوحة القيادة' : 'Skip to dashboard'}
          </button>
        </div>
      </div>
    </Layout>
  );
}
